import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import SettingsPricePage from './pages/SettingsPricePage';
import SettingsUsersPage from './pages/SettingsUsersPage';
import SettingsLanguagePage from './pages/SettingsLanguagePage';
import SettingsSliderPage from './pages/SettingsSliderPage';
import './App.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

interface User {
  id: string;
  username: string;
  role: string;
}

declare global {
  interface ImportMeta {
    env: {
      VITE_API_BASE_URL: string;
    };
  }
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
    const saved = localStorage.getItem('isAuthenticated');
    return saved === 'true';
  });
  const [user, setUser] = React.useState<User | null>(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });

  const handleLogin = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json() as { success: boolean; user?: User; error?: string };

      if (data.success) {
        const userData = data.user || null;
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        alert('Login failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
    }
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f5f7' }}>
          <Sidebar
            user={user}
            onLogout={handleLogout}
          />
          <div style={{ flex: 1 }}>
            <Routes>
              <Route path="/dashboard" element={<DashboardPage user={user} onLogout={handleLogout} />} />
              <Route path="/devices" element={<DevicesPage user={user} onLogout={handleLogout} />} />
              <Route path="/history" element={<HistoryPage user={user} onLogout={handleLogout} />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/devices" element={<DevicesPage user={user} onLogout={handleLogout} />} />
              <Route path="/settings/price" element={<SettingsPricePage />} />
              <Route path="/settings/users" element={<SettingsUsersPage />} />
              <Route path="/settings/language" element={<SettingsLanguagePage />} />
              <Route path="/settings/slider" element={<SettingsSliderPage />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </div>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App; 